// src/components/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import OfflineBar from "../components/utilities/OfflineBar";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  useMediaQuery,
  useTheme,
  FormHelperText,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// import cloudImage from '../img/image-log-reg.jpg';
import cloudImage from "../img/athrv-cloudimage-5.png";
import athrvLogo from "../img/athrv-cloud-logo-1.png";
import awsnew from "../img/aws-1.png";

const CustomTextField = styled(TextField)(({ theme }) => ({
  // When the label has not shrunk (data-shrink="false"), hide the input text
  '& .MuiInputLabel-root[data-shrink="false"] ~ .MuiInputBase-root input': {
    opacity: 0,
    transition: "opacity 0.3s ease", // optional smooth transition
  },
  // When the label is shrunk (data-shrink="true"), show the input text
  '& .MuiInputLabel-root[data-shrink="true"] ~ .MuiInputBase-root input': {
    opacity: 1,
    transition: "opacity 0.3s ease",
  },
  "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
    {
      WebkitBoxShadow: "0 0 0 1000px #e8f0fe inset !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
}));

// Validation schema using Yup
const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required")
    .max(254, "Email must be less than 254 characters")
    .test("includes-dot", "Email must contain at least one dot (.)", (value) =>
      value ? value.includes(".") : false
    ),

  password: Yup.string().required("Password is required"),
});

// Initial form values
const initialValues = {
  email: "",
  password: "",
  rememberMe: false,
};

const LogoHeader = () => {
  return (
    <Box
      component="img"
      src={athrvLogo}
      alt="Athrv Cloud"
      sx={{
        display: "block",
        mx: "auto", // shorthand for marginLeft and marginRight auto
        mb: 3,
        maxWidth: 150, // adjust to your desired max width
        height: "auto",
      }}
    />
  );
};

// Custom TextField component for Formik integration
const FormikTextField = ({ field, form: { touched, errors }, ...props }) => {
  const hasError = touched[field.name] && errors[field.name];

  return (
    <Box sx={{ mb: 2 }}>
      <CustomTextField
        {...field}
        {...props}
        fullWidth
        variant="outlined"
        error={hasError}
        helperText={hasError ? errors[field.name] : ""}
      />
    </Box>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [showOfflineBar, setShowOfflineBar] = useState(false);
  const [showBackOnlineBar, setShowBackOnlineBar] = useState(false);
  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, login, loading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // If already logged in, redirect to dashboard
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  

  // Login function: authenticate and store token and user details in context
  const loginUser = async (email, password) => {
    //setError(null);
    setShowOfflineBar(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );

      let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      console.error("Failed to parse JSON:", jsonError);
      setSnackbarMessage("Server error. Please try again later.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw new Error("Server error");
    }

    if (!response.ok) {
      // Extract error message from backend
      let errorMsg = "Login failed. Please try again.";
      
      if (data.error) {
        errorMsg = data.error;
      } else if (data.msg) {
        errorMsg = data.msg;
      } else if (data.message) {
        errorMsg = data.message;
      } else if (data.detail) {
        errorMsg = data.detail;
      }

      setSnackbarMessage(errorMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      throw new Error(errorMsg);
    }
      // Assume response contains { access_token, fname, user_role, user }
      // Call login to set the token, fname and role in centralized AuthContext
      // login(data.access_token, data.fname, data.user_role); // slow
      login(data.access_token, data.refresh_token, data.fname);
      return true;

    } catch (err) {
      setError(err.message);
      // Adjusted error handling to work in harmony with App.js's global fetch override.
      if (err.message === "No Internet Connection") {
        setShowOfflineBar(true);
      } else if (err.message === "Server under maintenance") {
        // Let the global error dialog handle this scenario.
      } else if (!snackbarOpen) {
      setSnackbarMessage(err.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
      return false;
    }
  };

  // Function to check that all key local storage items are set
  const confirmLocalStorageItems = () => {
    const token = localStorage.getItem("access_token");
    const fname = localStorage.getItem("fname");
    return token && fname;
  };

  // Form submission handler using Formik
  const handleFormSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const success = await loginUser(values.email, values.password);

      if (success) {
        if (values.rememberMe) {
          localStorage.setItem("remember_me", "true");
        }

        // Wait briefly (if needed) and confirm that all major local storage items are set.
        if (confirmLocalStorageItems()) {
          navigate("/dashboard");
        } else {
          console.error("Local storage items missing. Not redirecting.");
          setSnackbarMessage("Authentication failed. Please try again.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }
    } catch (err) {
      // Handle specific field errors if needed
      if (err.message.toLowerCase().includes("email")) {
        setFieldError("email", err.message);
      } else if (err.message.toLowerCase().includes("password")) {
        setFieldError("password", err.message);
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Retry handler for OfflineBar
  const handleRetry = async () => {
    const success = await loginUser();
    if (success) {
      setShowBackOnlineBar(true);
      setTimeout(() => {
        setShowBackOnlineBar(false);
        navigate("/dashboard");
      }, 3000);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      {/* Left Section: Background Image (Desktop Only) */}
      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: "50%",
            backgroundImage: `url(${cloudImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}

      {/* Right Section: Login Form */}
      <Box
        sx={{
          ml: !isMobile ? "50%" : 0,
          // height: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          scrollbarGutter: "stable",
          // pt: isMobile ? 4 : 2, // extra padding top for mobile
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            background: "rgba(255, 255, 255, 0.2)",
            width: isMobile ? "90%" : "400px",
          }}
        >
          {/* <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
            Athrv Cloud
          </Typography> */}
          <LogoHeader />
          <Typography
          variant="body1"
          sx={{color: "#555", textAlign: "center" }}
        >
          AI Powered Unified DevOps Platform
        </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={loginValidationSchema}
            onSubmit={handleFormSubmit}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ isSubmitting, values, setFieldValue, errors, touched }) => (
              <Form>
                <Field
                  name="email"
                  component={FormikTextField}
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                />

                <Field
                  name="password"
                  component={FormikTextField}
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.rememberMe}
                      onChange={(e) =>
                        setFieldValue("rememberMe", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Remember Me"
                  sx={{ mb: 2 }}
                />

                

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: "#0C1A32",
                    "&:hover": { backgroundColor: "#002244" },
                    mb: 2,
                    py: 1.5,
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Log In"
                  )}
                </Button>
              </Form>
            )}
          </Formik>

          <Typography
            variant="body2"
            align="center"
            sx={{
              cursor: "pointer",
              color: theme.palette.primary.main,
              textDecoration: "underline",
              mb: 2,
            }}
            onClick={() => navigate("/forgot-password")}
          >
            Forgot Password?
          </Typography>

          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            Don't have an Account?{" "}
            <a
              onClick={() => navigate("/signup")}
              style={{
                color: theme.palette.primary.main,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              SignUp
            </a>
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            <a
              href="https://athrvcloud.com"
              style={{
                color: theme.palette.primary.main,
                textDecoration: "underline",
              }}
            >
              Back to Home
            </a>
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <Typography variant="body2" sx={{ fontSize: "20px", mr: 1 }}>
              Powered By
            </Typography>
            <img
              src={awsnew}
              alt="AWS Logo"
              style={{ height: "30px", marginTop: "12px" }}
            />
          </Box>
        </Paper>
      </Box>
      {/* Snackbar for error/success messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Offline Notifications */}
      <OfflineBar
        open={showOfflineBar}
        message="No Internet Connection"
        onRetry={handleRetry}
        onClose={() => setShowOfflineBar(false)}
      />
      <OfflineBar
        open={showBackOnlineBar}
        message="Back Online"
        isOnline
        onClose={() => setShowBackOnlineBar(false)}
      />
    </Box>
  );
};

export default LoginPage;
